import { useEffect, useMemo, useState } from 'react';
import { oneLine } from 'common-tags';

import X from '@/icons/light/x.svg';
import { Container } from '@/atoms';
import { CardFlat } from '@/molecules';

import { cardTransformer } from '@/graphql/transformers';
import { blockSpacing, sectionBgProps, textStyleClass } from '@/utils';

import 'leaflet/dist/leaflet.css';

/**
 * Resuelve las coordenadas de un marker con sistema de prioridades:
 * 1. Coordenadas del inmueble (zfm_disponiblidad)
 * 2. Coordenadas manuales (Latitud/Longitud)
 */
// Precedencia: coordenadas del inmueble > coordenadas manuales del marker.
const resolveMarkerLocation = (marker: any) => {
  const property = marker?.zfm_disponiblidad;

  // Prioridad 1: Coordenadas del inmueble
  if (property?.latitud && property?.longitud) {
    const lat = parseFloat(property.latitud);
    const lng = parseFloat(property.longitud);
    if (!isNaN(lat) && !isNaN(lng)) {
      return { lat, lng, property };
    }
  }

  // Prioridad 2: Coordenadas manuales
  if (marker?.Latitud && marker?.Longitud) {
    const lat = parseFloat(String(marker.Latitud));
    const lng = parseFloat(String(marker.Longitud));
    if (!isNaN(lat) && !isNaN(lng)) {
      return { lat, lng, property: property || null };
    }
  }

  return null;
};

/**
 * ComponentRevisadasPropertyMap
 */
export const ComponentRevisadasPropertyMap = ({
  Titulo,
  Descripcion,
  TituloEstilo,
  DescripcionEstilo,
  Markers,
  DefaultZoom = 17,
  MinHeight = '400px',
  PaddingVertical,
  FondoSeccion,
  Espaciado,
}: // eslint-disable-next-line @typescript-eslint/no-explicit-any
any) => {
  const tituloCls = textStyleClass(TituloEstilo);
  const descripcionCls = textStyleClass(DescripcionEstilo);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [rLeaflet, setRLeaflet] = useState<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [leaflet, setLeaflet] = useState<any>(null);
  const [openMarkerId, setOpenMarkerId] = useState<number | null>(null);

  useEffect(() => {
    (async () => {
      const rl = await import('react-leaflet');
      const L = await import('leaflet');
      setRLeaflet(rl);
      setLeaflet(L);
    })();
  }, []);

  // Resolver coordenadas de todos los markers
  const resolvedMarkers = useMemo(
    () =>
      (Markers || [])
        .map((marker: any, index: number) => {
          const resolved = resolveMarkerLocation(marker);
          return resolved ? { ...resolved, id: marker.id || index } : null;
        })
        .filter(Boolean),
    [Markers],
  );

  if (resolvedMarkers.length === 0 || !leaflet || !rLeaflet) {
    return null;
  }

  const { MapContainer, TileLayer, Marker: LeafletMarker } = rLeaflet;
  const icon = leaflet.icon({
    iconUrl: '/assets/images/marker.svg',
    iconSize: [38, 95],
    shadowSize: [50, 64],
    iconAnchor: [22, 94],
    shadowAnchor: [4, 62],
    popupAnchor: [-3, -76],
  });

  // Calcular el centro del mapa basado en los markers
  const center =
    resolvedMarkers.length === 1
      ? { lat: resolvedMarkers[0].lat, lng: resolvedMarkers[0].lng }
      : {
          lat:
            resolvedMarkers.reduce((sum: number, m: any) => sum + m.lat, 0) /
            resolvedMarkers.length,
          lng:
            resolvedMarkers.reduce((sum: number, m: any) => sum + m.lng, 0) /
            resolvedMarkers.length,
        };

  // Calcular bounds para fitBounds cuando hay múltiples markers
  const bounds =
    resolvedMarkers.length > 1
      ? resolvedMarkers.map((m: any) => [m.lat, m.lng])
      : undefined;

  // Datos del marker seleccionado para el popup
  const selectedMarker = resolvedMarkers.find(
    (m: any) => m.id === openMarkerId,
  );
  const selectedProperty = selectedMarker?.property;

  return (
    <Container
      wrapper
      classNameWrapper={
        Espaciado
          ? blockSpacing(Espaciado)
          : PaddingVertical
            ? 'py-6'
            : undefined
      }
      {...sectionBgProps(FondoSeccion)}
    >
      {(Titulo || Descripcion) && (
        <div className="mb-6">
          {Titulo && (
            <h2
              className={oneLine`mb-2 text-gray-900 ${
                tituloCls ?? 'text-2xl font-bold md:text-3xl'
              }`}
            >
              {Titulo}
            </h2>
          )}
          {Descripcion && (
            <p className={oneLine`text-gray-600 ${descripcionCls ?? ''}`}>
              {Descripcion}
            </p>
          )}
        </div>
      )}
      <div className="relative">
        <MapContainer
          attributionControl={false}
          center={[center.lat, center.lng]}
          zoom={bounds ? undefined : DefaultZoom}
          bounds={bounds}
          boundsOptions={{ padding: [50, 50] }}
          scrollWheelZoom={false}
          style={{ minHeight: MinHeight }}
        >
          <TileLayer
            url="http://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}&hl=es&gl=ES"
            maxZoom={20}
            subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
            attribution=""
          />
          {resolvedMarkers.map((marker: any) => (
            <LeafletMarker
              key={marker.id}
              position={[marker.lat, marker.lng]}
              icon={icon}
              eventHandlers={
                marker.property
                  ? {
                      click: () => setOpenMarkerId(marker.id),
                    }
                  : undefined
              }
            />
          ))}
        </MapContainer>

        {selectedProperty && (
          <div
            className="pointer-events-auto absolute top-48 left-1/2 z-1000 -translate-x-1/2"
            style={{ maxWidth: '300px' }}
          >
            <div className="relative mt-6 w-[300px]">
              <CardFlat
                {...cardTransformer(
                  selectedProperty,

                  () => {},
                )}
                className="max-w-xs"
              />
              <button
                className="bg-neutrals-0/60 hover:bg-neutrals-0/80 absolute top-2.5 left-4 flex h-8 w-8 items-center justify-center rounded-full"
                onClick={() => setOpenMarkerId(null)}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </Container>
  );
};

ComponentRevisadasPropertyMap.displayName = 'ComponentRevisadasPropertyMap';
